import {
  CTCSS_TONES,
  DTCS_CODES,
  compareImages,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 0x20
const SLOT_COUNT = 107
const SETTINGS_ADDRESS = 0xe000
const MEMORY_SIZE = SETTINGS_ADDRESS + BLOCK_SIZE
const DCS_BASE = 0x2800
const REPEATER_OFFSET = 5_000_000
const IDENTITY = 'SMP558'
const BANDS = [
  [462_550_000, 462_737_500],
  [467_550_000, 467_737_500],
] as const

const PLAIN_REPEATER_RX = new Map<number, number>([
  [16, 462_550_000],
  [18, 462_575_000],
  [20, 462_600_000],
  [22, 462_625_000],
  [24, 462_650_000],
  [26, 462_675_000],
  [28, 462_700_000],
  [30, 462_725_000],
])

export type MXT295Profile = {
  protocol: 'mxt295'
  id: string
  vendor: 'Midland'
  model: 'MXT295'
  summary: string
  tags: readonly string[]
  baudRate: 115200
  channelMinimum: 1
  channelMaximum: 107
  nameLength: 8
  powerLevels: readonly ['Low', 'High']
  memorySize: number
}

export const MXT295_PROFILES: readonly MXT295Profile[] = [
  {
    protocol: 'mxt295',
    id: 'midland-mxt295',
    vendor: 'Midland',
    model: 'MXT295',
    summary:
      'Hardware-tested MXT295 programming with Midland channel labels and GMRS safety rules.',
    tags: [
      'Experimental',
      'MXPG15',
      'Read + write',
      '99 Midland channels',
      '8-character names',
    ],
    baudRate: 115200,
    channelMinimum: 1,
    channelMaximum: SLOT_COUNT,
    nameLength: 8,
    powerLevels: ['Low', 'High'],
    memorySize: MEMORY_SIZE,
  },
]

export function mxt295ChannelLabel(number: number): string {
  if (number <= 15) return String(number)
  if (number <= 30) {
    const offset = number - 15
    const channel = 15 + Math.floor(offset / 2)
    return offset % 2 === 1 ? `RPTR ${channel}` : String(channel)
  }
  return String(number - 8)
}

function gmrsRx(channel: number): number | null {
  if (channel >= 1 && channel <= 7) {
    return 462_562_500 + (channel - 1) * 25_000
  }
  if (channel >= 8 && channel <= 14) {
    return 467_562_500 + (channel - 8) * 25_000
  }
  if (channel >= 15 && channel <= 22) {
    return 462_550_000 + (channel - 15) * 25_000
  }
  return null
}

function gmrsChannel(frequency: number): number | null {
  for (let channel = 1; channel <= 22; channel += 1) {
    if (gmrsRx(channel) === frequency) return channel
  }
  return null
}

function slotGmrsChannel(number: number): number | null {
  if (number >= 1 && number <= 15) return number
  if (number >= 16 && number <= 30) {
    return 15 + Math.floor((number - 15) / 2)
  }
  return null
}

function isInBand(frequency: number): boolean {
  return BANDS.some(
    ([minimum, maximum]) => frequency >= minimum && frequency < maximum,
  )
}

function isRepeaterOutput(frequency: number): boolean {
  return (
    frequency >= 462_550_000 &&
    frequency <= 462_725_000 &&
    (frequency - 462_550_000) % 25_000 === 0
  )
}

function emptyChannel(
  number: number,
  profile: MXT295Profile,
): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: profile.powerLevels[0],
    skip: false,
  }
}

type ToneSpec = {
  mode: WebChannel['toneMode']
  ctcss: number | null
  dtcs: number | null
  dtcsPolarity: 'N' | 'R'
}

function decodeToneWord(word: number): ToneSpec {
  if (word === 0 || word === 0xffff) {
    return { mode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  if (word >= DCS_BASE) {
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: Number.parseInt((word - DCS_BASE).toString(8).padStart(3, '0'), 10),
      dtcsPolarity: 'N',
    }
  }
  return {
    mode: 'ctcss',
    ctcss: word / 10,
    dtcs: null,
    dtcsPolarity: 'N',
  }
}

function decodeName(raw: Uint8Array): string {
  let end = raw.length
  for (let index = 0; index < raw.length; index += 1) {
    if (raw[index] === 0 || raw[index] === 0xff) {
      end = index
      break
    }
  }
  return new TextDecoder('ascii')
    .decode(raw.slice(0, end))
    .replace(/[^\x20-\x7e]/g, '')
    .trimEnd()
}

export function parseMXT295Channel(
  image: Uint8Array,
  profile: MXT295Profile,
  number: number,
): WebChannel {
  if (number < profile.channelMinimum || number > profile.channelMaximum) {
    throw new Error(`Channel ${number} is outside the MXT295 range.`)
  }
  const offset = (number - profile.channelMinimum) * BLOCK_SIZE
  const raw = image.slice(offset, offset + BLOCK_SIZE)
  if (raw.byteLength !== BLOCK_SIZE) return emptyChannel(number, profile)
  const view = new DataView(raw.buffer, raw.byteOffset, raw.byteLength)
  const rxFrequency = view.getUint32(8, false)
  if (rxFrequency === 0) return emptyChannel(number, profile)
  const txRaw = view.getUint32(14, false)
  const txFrequency = txRaw === 0 ? null : txRaw
  const rxTone = decodeToneWord(view.getUint16(12, false))
  const txTone = decodeToneWord(view.getUint16(18, false))
  const selectedTone =
    txFrequency === null
      ? rxTone
      : rxTone.mode !== 'none'
        ? rxTone
        : txTone
  const flags = raw[20]
  return {
    number,
    empty: false,
    name: decodeName(raw.slice(0, 8)),
    rxFrequency,
    txFrequency,
    toneMode: selectedTone.mode,
    ctcss: selectedTone.ctcss,
    dtcs: selectedTone.dtcs,
    dtcsPolarity: selectedTone.dtcsPolarity,
    mode: (flags & 0x08) !== 0 ? 'FM' : 'NFM',
    power: (flags & 0x02) !== 0 ? 'High' : 'Low',
    skip: (flags & 0x10) === 0,
  }
}

export function parseMXT295Channels(
  image: Uint8Array,
  profile: MXT295Profile,
): WebChannel[] {
  return Array.from(
    { length: profile.channelMaximum },
    (_, index) => parseMXT295Channel(image, profile, index + 1),
  )
}

export function mxt295ChannelIssues(channel: WebChannel): string[] {
  if (channel.empty) return []
  const rxFrequency = channel.rxFrequency
  if (!rxFrequency) return ['Choose a receive frequency.']
  const issues: string[] = []
  const offset = Math.round((channel.txFrequency ?? rxFrequency) - rxFrequency)
  const number = channel.number
  const fixedChannel = slotGmrsChannel(number)
  const fixedFrequency =
    fixedChannel === null ? null : gmrsRx(fixedChannel)
  if (fixedFrequency !== null && rxFrequency !== fixedFrequency) {
    issues.push(
      `Channel ${mxt295ChannelLabel(number)} must stay on ${(
        fixedFrequency / 1_000_000
      ).toFixed(6)} MHz.`,
    )
  }
  if (number >= 8 && number <= 14 && channel.txFrequency !== null) {
    issues.push('Channels 8 through 14 stay receive-only.')
  }
  if (PLAIN_REPEATER_RX.has(number) && offset !== REPEATER_OFFSET) {
    issues.push('Repeater rows use a +5 MHz offset.')
  }
  if (
    number >= 1 &&
    number <= 7 &&
    (channel.txFrequency === null || offset !== 0)
  ) {
    issues.push('Channels 1 through 14 stay simplex.')
  }
  const pickedChannel =
    fixedChannel === null ? gmrsChannel(rxFrequency) : null
  if (
    pickedChannel !== null &&
    pickedChannel <= 14 &&
    (channel.txFrequency === null || offset !== 0)
  ) {
    issues.push('Channels 1 through 14 stay simplex.')
  }
  if (offset !== 0) {
    if (offset !== REPEATER_OFFSET) {
      issues.push('Repeater offset must be +5 MHz.')
    }
    if (!isRepeaterOutput(rxFrequency)) {
      issues.push(
        'A +5 MHz offset is only for repeater outputs 462.550 through 462.725 MHz.',
      )
    }
  }
  if (rxFrequency % 12_500 !== 0) {
    issues.push('Receive frequency must be on a 12.5 kHz channel.')
  }
  if (!isInBand(rxFrequency)) {
    issues.push('Receive frequency is outside the MXT295 GMRS bands.')
  }
  if (channel.txFrequency !== null) {
    if (channel.txFrequency % 12_500 !== 0) {
      issues.push('Transmit frequency must be on a 12.5 kHz channel.')
    }
    if (!isInBand(channel.txFrequency)) {
      issues.push('Transmit frequency is outside the MXT295 GMRS bands.')
    }
  }
  return issues
}

function encodeName(name: string): Uint8Array {
  const normalized = name.trim().toUpperCase().slice(0, 8)
  if (!/^[A-Z0-9 ]*$/.test(normalized)) {
    throw new Error('Use uppercase letters, numbers, and spaces in the name.')
  }
  const result = new Uint8Array(8)
  result.set(new TextEncoder().encode(normalized.padEnd(8, ' ')))
  return result
}

function encodeToneWord(channel: WebChannel): number {
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    if (!CTCSS_TONES.includes(channel.ctcss as (typeof CTCSS_TONES)[number])) {
      throw new Error('That CTCSS tone is not supported by the MXT295.')
    }
    return Math.round(channel.ctcss * 10)
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    if (!DTCS_CODES.includes(channel.dtcs as (typeof DTCS_CODES)[number])) {
      throw new Error(`DCS code ${channel.dtcs} is not supported by the MXT295.`)
    }
    return DCS_BASE + Number.parseInt(channel.dtcs.toString(), 8)
  }
  return 0xffff
}

function clearSlot(raw: Uint8Array): void {
  raw.fill(0)
  raw.fill(0xff, 12, 14)
  raw.fill(0xff, 18, 20)
}

function writePlainRepeater(raw: Uint8Array, rxFrequency: number): void {
  raw.fill(0)
  const view = new DataView(raw.buffer, raw.byteOffset, raw.byteLength)
  view.setUint32(8, rxFrequency, false)
  view.setUint16(12, 0xffff, false)
  view.setUint32(14, rxFrequency + REPEATER_OFFSET, false)
  view.setUint16(18, 0xffff, false)
  raw[20] = 0x0b
}

function toneMatches(channel: WebChannel, tone: ToneSpec): boolean {
  return (
    channel.toneMode === tone.mode &&
    channel.ctcss === tone.ctcss &&
    channel.dtcs === tone.dtcs
  )
}

export function applyMXT295Channel(
  image: Uint8Array,
  profile: MXT295Profile,
  channel: WebChannel,
): void {
  const offset = (channel.number - profile.channelMinimum) * BLOCK_SIZE
  const raw = image.subarray(offset, offset + BLOCK_SIZE)
  const view = new DataView(raw.buffer, raw.byteOffset, raw.byteLength)
  const rxBefore = view.getUint32(8, false)
  if (channel.empty) {
    const plainRepeater = PLAIN_REPEATER_RX.get(channel.number)
    if (plainRepeater !== undefined) {
      writePlainRepeater(raw, plainRepeater)
    } else if (rxBefore !== 0) {
      clearSlot(raw)
    }
    return
  }
  const issues = mxt295ChannelIssues(channel)
  if (issues.length) throw new Error(issues[0])
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')

  const previous = parseMXT295Channel(image, profile, channel.number)
  const previousRxTone = decodeToneWord(view.getUint16(12, false))
  const previousTxTone = decodeToneWord(view.getUint16(18, false))
  const preserveSplit =
    previousRxTone.mode !== previousTxTone.mode ||
    previousRxTone.ctcss !== previousTxTone.ctcss ||
    previousRxTone.dtcs !== previousTxTone.dtcs
  const toneUnchanged = toneMatches(channel, {
    mode: previous.toneMode,
    ctcss: previous.ctcss,
    dtcs: previous.dtcs,
    dtcsPolarity: previous.dtcsPolarity,
  })
  const sameTransmitMode =
    (previous.txFrequency === null) === (channel.txFrequency === null)

  raw.set(encodeName(channel.name), 0)
  view.setUint32(8, channel.rxFrequency, false)
  view.setUint32(14, channel.txFrequency ?? 0, false)
  if (!(preserveSplit && toneUnchanged && sameTransmitMode)) {
    const tone = encodeToneWord(channel)
    view.setUint16(12, tone, false)
    view.setUint16(18, channel.txFrequency === null ? 0xffff : tone, false)
  }
  const unknown = raw[20] & 0xe4
  raw[20] =
    unknown |
    (channel.txFrequency !== null &&
    channel.txFrequency !== channel.rxFrequency
      ? 0x01
      : 0) |
    (channel.power === 'High' ? 0x02 : 0) |
    (channel.mode === 'FM' ? 0x08 : 0) |
    (channel.skip ? 0 : 0x10)
}

function blockAddresses(): number[] {
  return [
    ...Array.from({ length: SLOT_COUNT }, (_, index) => index * BLOCK_SIZE),
    SETTINGS_ADDRESS,
  ]
}

async function readAck(
  transport: RadioTransport,
  label: string,
): Promise<void> {
  if ((await transport.readExact(1, 2500))[0] !== ACK) {
    throw new Error(`The MXT295 did not acknowledge ${label}.`)
  }
}

async function enterProgrammingMode(
  transport: RadioTransport,
): Promise<string> {
  await transport.discardInput()
  await transport.write(
    Uint8Array.from([0x02, ...new TextEncoder().encode('PROGRAM')]),
  )
  await readAck(transport, 'programming mode')
  await transport.write(Uint8Array.of(0x02))
  const identity = await transport.readExact(8, 3000)
  if (
    new TextDecoder('ascii').decode(identity.slice(0, IDENTITY.length)) !==
    IDENTITY
  ) {
    throw new Error(
      'The connected radio did not identify as a Midland MXT295 (SMP558).',
    )
  }
  await transport.write(Uint8Array.of(ACK))
  await readAck(transport, 'the programming session')
  return new TextDecoder('ascii')
    .decode(identity)
    .replace(/[^\x20-\x7e]/g, '')
    .trim()
}

async function exitProgrammingMode(
  transport: RadioTransport,
): Promise<void> {
  await transport.write(Uint8Array.of(0x62))
}

async function readBlock(
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  const command = Uint8Array.of(
    0x52,
    (address >> 8) & 0xff,
    address & 0xff,
    BLOCK_SIZE,
  )
  await transport.write(command)
  const response = await transport.readExact(4 + BLOCK_SIZE, 3000)
  if (compareImages(command, response.slice(0, 4)) !== null) {
    throw new Error(
      `Unexpected MXT295 read reply at 0x${address.toString(16)}.`,
    )
  }
  return response.slice(4)
}

async function writeBlock(
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  const command = new Uint8Array(4 + BLOCK_SIZE)
  command.set(
    Uint8Array.of(
      0x57,
      (address >> 8) & 0xff,
      address & 0xff,
      BLOCK_SIZE,
    ),
  )
  command.set(data, 4)
  await transport.write(command)
  await readAck(transport, `write block 0x${address.toString(16)}`)
}

export async function readMXT295RadioWithTransport(
  profile: MXT295Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  const reportedModel = await enterProgrammingMode(transport)
  try {
    const image = new Uint8Array(profile.memorySize).fill(0xff)
    const addresses = blockAddresses()
    for (let index = 0; index < addresses.length; index += 1) {
      const address = addresses[index]
      image.set(await readBlock(transport, address), address)
      onProgress?.({
        current: index + 1,
        total: addresses.length,
        label: 'Reading Midland MXT295 memory',
      })
    }
    return { image, reportedModel: reportedModel || profile.model }
  } finally {
    await exitProgrammingMode(transport)
  }
}

export async function writeMXT295RadioWithTransport(
  profile: MXT295Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < profile.memorySize) {
    throw new Error('The selected image is smaller than the MXT295 memory map.')
  }
  await enterProgrammingMode(transport)
  try {
    const addresses = blockAddresses()
    for (let index = 0; index < addresses.length; index += 1) {
      const address = addresses[index]
      await writeBlock(
        transport,
        address,
        image.slice(address, address + BLOCK_SIZE),
      )
      onProgress?.({
        current: index + 1,
        total: addresses.length,
        label: 'Writing Midland MXT295 memory',
      })
    }
  } finally {
    await exitProgrammingMode(transport)
  }
}
